/**
 * The Broadcast detail screen's data layer: one id in, one decoded funnel out,
 * kept fresh for exactly as long as the send is still running.
 *
 * Hand-rolled useState/useEffect, matching `useBroadcastList.js` and
 * `useBroadcastEstimate.js` — this app carries neither react-query nor SWR, and
 * a hook is not the place to add a dependency. The fetch idiom is the same one:
 * a cancelled flag owned by the effect plus a generation counter in a ref,
 * combined into a single `isStale()` gate a response has to pass before it is
 * believed.
 *
 * ---------------------------------------------------------------------------
 * ONE REQUEST LOADS THIS SCREEN
 * ---------------------------------------------------------------------------
 * `GET /api/v1/broadcasts/{id}/funnel` returns `{ broadcast, by_channel,
 * combined, notes }` — the row itself is IN the funnel response. So there is no
 * separate `GET /api/v1/broadcasts/{id}` call here and none should be added:
 * a second request for the row would double the traffic of every poll tick to
 * re-fetch fields the first response already carried, and would introduce the
 * possibility of the header and the numbers disagreeing about the status.
 * `broadcast` below is a convenience alias for `funnel.broadcast`, not a
 * separately-fetched thing.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE OWNS AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * It owns request orchestration: when to fire, when to keep firing, which
 * response to believe, and what a failure means for the screen. It owns no wire
 * names — the response shape comes from `broadcastDecode.js`, the meaning of a
 * status from `broadcastStatus.js`, and the copy for a failure from
 * `broadcastErrors.js`. Consumers receive camelCase and a human string, never a
 * raw payload and never an `err.message`.
 *
 * ---------------------------------------------------------------------------
 * IT ONLY SETS STATE — IT NEVER MOVES THE USER
 * ---------------------------------------------------------------------------
 * A poll tick lands while someone is reading. Nothing in this file calls
 * `focus()`, scrolls, or opens anything, and nothing added here ever should: a
 * background refresh that steals focus every 15 seconds makes the page
 * unusable for keyboard and screen-reader users, and it does it silently for
 * everyone else. Announcing an update is the consuming component's job, via an
 * `aria-live="polite"` region around the counters — polite so it queues behind
 * whatever the user is doing instead of interrupting it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { get as commsGet, post as commsPost } from "../../../service/comms/commsApi";
import { getFunnel, retractBroadcast } from "../../../service/comms/broadcastsService";
import { funnelFromPayload, retractNoteFrom } from "./broadcastDecode";
import { isForbidden, isNotFound, messageFor } from "./broadcastErrors";
import { statusMeta } from "./broadcastStatus";
import { SENDING_POLL_MS } from "./broadcastEnums";

/**
 * Load one broadcast's delivery funnel, and keep loading it while it sends.
 *
 * @param {string|null|undefined} id - Broadcast id.
 * @returns {{
 *   funnel: Object|null,
 *   broadcast: Object|null,
 *   loading: boolean,
 *   refreshing: boolean,
 *   error: string|null,
 *   notFound: boolean,
 *   forbidden: boolean,
 *   polling: boolean,
 *   retract: () => Promise<{ ok: boolean, note: string|null }>,
 *   retracting: boolean,
 *   retractNote: string|null,
 *   refresh: () => void,
 * }}
 */
export default function useBroadcastDetail(id) {
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [polling, setPolling] = useState(false);

  const [retracting, setRetracting] = useState(false);
  const [retractNote, setRetractNote] = useState(null);

  /**
   * Bumped by `refresh()` — and by a successful `retract()` — to re-run the
   * effect with an unchanged id. A counter rather than a boolean so two
   * refreshes in a row both land.
   */
  const [refreshToken, setRefreshToken] = useState(0);

  /**
   * The generation of the most recently STARTED fetch. Paired with the effect's
   * own `cancelled` flag: the flag says "my effect was replaced", the ref says
   * "someone newer is authoritative now". The pair matters more here than
   * anywhere else in this feature, because a poll tick starts a fetch from
   * inside a timer callback rather than from the effect body — a response can
   * therefore arrive long after the effect that scheduled it was torn down.
   */
  const generationRef = useRef(0);

  /**
   * The funnel we currently hold, mirrored into a ref.
   *
   * Read inside the fetch to decide first-load vs. refresh. It has to be a ref
   * and not the state value, because the poll loop re-enters through a timer
   * closure that captured the render it was created in — reading `funnel` there
   * would see the value from 15 seconds ago, and the second tick of a long send
   * would announce itself as a first load.
   */
  const funnelRef = useRef(null);

  /** The id the currently-held funnel belongs to. See the reset below. */
  const lastIdRef = useRef(undefined);

  useEffect(() => {
    /*
     * Changing the id means the funnel on screen describes a DIFFERENT
     * broadcast. It is dropped immediately rather than left up until the new
     * response lands: showing broadcast A's delivery numbers under broadcast
     * B's title is worse than showing a skeleton, because nothing about it
     * looks wrong. A `refresh()` deliberately does NOT take this branch — same
     * broadcast, so the numbers on screen are still that broadcast's numbers.
     */
    if (lastIdRef.current !== id) {
      lastIdRef.current = id;
      funnelRef.current = null;
      setFunnel(null);
      setRetractNote(null);
      setError(null);
      setNotFound(false);
      setForbidden(false);
    }

    if (!id) {
      setLoading(false);
      setRefreshing(false);
      setPolling(false);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    /** A response is worth believing only if it is both live and current. */
    const isStale = () => cancelled || generationRef.current !== generation;

    /**
     * Arm the next tick — or don't.
     *
     * THE DECISION IS RE-EVALUATED ON EVERY RESPONSE, never once at mount.
     * A send that finishes between two ticks must stop the timer, and it can
     * only do that if the status that just came back is the one being read. A
     * naive implementation reads `polls` once, sees `sending`, and keeps asking
     * forever — for every finished broadcast anyone opens, for as long as the
     * tab stays open.
     */
    const schedule = (decoded) => {
      const shouldPoll = statusMeta(decoded?.broadcast).polls === true;
      setPolling(shouldPoll);
      if (!shouldPoll) return;
      // A 100k send takes hours by design, so this is a progress ticker, not a
      // race. The interval lives in `broadcastEnums.js`.
      timer = setTimeout(load, SENDING_POLL_MS);
    };

    const load = () => {
      /*
       * The distinction the whole screen rests on. `loading` means "there is
       * nothing to show yet" and drives the skeleton; `refreshing` means "the
       * numbers below are about to be replaced" and drives a hairline spinner.
       * Setting `loading` on a poll tick would tear the funnel off the screen
       * and flash a skeleton every 15 seconds at someone who is mid-sentence
       * reading it.
       */
      if (funnelRef.current === null) setLoading(true);
      else setRefreshing(true);

      getFunnel(commsGet, id)
        .then((payload) => {
          if (isStale()) return;
          const decoded = funnelFromPayload(payload);
          funnelRef.current = decoded;
          setFunnel(decoded);
          setError(null);
          setNotFound(false);
          setForbidden(false);
          setLoading(false);
          setRefreshing(false);
          schedule(decoded);
        })
        .catch((err) => {
          if (isStale()) return;
          // Never `err.message` — `messageFor` is the only place that decides
          // what a status code says to a person.
          setError(messageFor(err));
          // Both are kept separate from `error` on purpose. A 404 or a 403
          // means the page REPLACES itself with a not-found or an access
          // notice; an ordinary failure decorates the page it already has.
          // The 404 copy must never mention permission — see `messageFor`.
          setNotFound(isNotFound(err));
          setForbidden(isForbidden(err));
          setLoading(false);
          setRefreshing(false);
          // Nothing is armed after a failure. A screen that cannot load is not
          // a screen worth re-loading on a timer.
          setPolling(false);
        });
    };

    load();

    /*
     * Both halves matter. Clearing the timer stops a tick that has not fired;
     * flipping `cancelled` drops the response of one already in flight. Leaving
     * either behind is a request every 15 seconds for the lifetime of the tab,
     * against a broadcast nobody is looking at any more.
     */
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  /**
   * Stop this broadcast, then re-read the funnel.
   *
   * The refetch is not cosmetic: retraction changes the status badge and the
   * counters, and the response to the retract itself carries neither. Failure
   * leaves the funnel exactly as it was — the send is still whatever it was
   * before we asked, and blanking the page would suggest otherwise.
   */
  const retract = useCallback(async () => {
    if (!id) return { ok: false, note: null };

    setRetracting(true);
    setError(null);

    try {
      const response = await retractBroadcast(commsPost, id);
      // Verbatim. The note states plainly what retraction cannot undo (a push
      // already on a device stays on it); paraphrasing a statement about what
      // has already happened is how a user ends up believing it was recalled.
      const note = retractNoteFrom(response);
      setRetractNote(note);
      setRetracting(false);
      refresh();
      return { ok: true, note };
    } catch (err) {
      setError(messageFor(err));
      // Deliberately not `notFound`/`forbidden`: the funnel loaded fine, so the
      // page is not gone and the user is not locked out. A failed retract is a
      // failed ACTION, and it belongs next to the button, not in place of the
      // screen. (423 — already retracted — is the common one here.)
      setRetracting(false);
      return { ok: false, note: null };
    }
  }, [id, refresh]);

  return {
    funnel,
    /** Convenience alias. One request produced both; see the header. */
    broadcast: funnel ? funnel.broadcast : null,
    loading,
    refreshing,
    error,
    notFound,
    forbidden,
    polling,
    retract,
    retracting,
    retractNote,
    refresh,
  };
}
